import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import type { UsePipelineTargetsReturn } from '../../../hooks/pipelineTracker/usePipelineTargets';
import {
  DEFAULT_RAG,
  MAX_PARTNER_OVERRIDES,
  PARTNER_TARGET_KEY_PREFIX,
  TARGET_STAGES,
  boundedDecimalInput,
  centsToDollarsInput,
  dollarsInputToCents,
  monthLabel,
  recentMonthKeys,
  wholeNumberInput,
  type BrokerTargets,
  type MonthlyInput,
  type MonthlyInputTargets,
  type StageTargets,
  type TargetStageKey,
} from '../../../hooks/pipelineTracker/targetsContract';
import { netSettlementsHeadlineLabel } from './netBook';

/**
 * Targets and monthly inputs: the administrator's editor, under the 12-month trend.
 *
 * WHERE, AND WHY THERE. Beside the figures it grades, for the reason the broker editor sits under
 * the broker table: a target is set by looking at what the practice actually does, and the trend
 * above is exactly that. A separate settings page would mean setting a target without the one
 * chart that makes a sensible one obvious.
 *
 * WHO. Rendered only for canEditTaxonomy(role), the same rule as the roster and stage mapping. The
 * callable is the real gate (company-wide super_admin or administrator, founder blocked from
 * writes, every save audited); this only keeps a control off a screen whose viewer would be
 * refused.
 *
 * WHAT IT STORES, AND WHAT IT DOES NOT. Monthly stage targets (deals and dollars), per-broker
 * overrides, the red/amber/green thresholds, SLA targets for DIP and MIR (stored now, graded when
 * the Operations overview ships), and the monthly figures no export carries: Google reviews count
 * and rating, and the SFG audit result, each with an optional target that grades its tile on
 * Operations > Overview. Blank is "not set", never zero. Dollars are whole dollars, stored as
 * integer cents.
 *
 * BRIEF GAPS (2026-10-20): the average settled deal target (company and per broker), the net
 * settlements target (company only, named against the Board pack's headline loan-book figure, read
 * from netBook.ts so the label and the grading cannot disagree), and referrer targets for the four
 * stages. A referrer is picked from the Leads & Partners partner list (`partnerOptions`), never
 * typed: the key is the report's `id:<Referrer Ref>` and the server refuses any other form. When no
 * list is passed, stored referrer targets are still shown and editable, and the section says why
 * none can be added here.
 *
 * CLEARING. The scalar targets are always sent, as a number or null, and the referrer map is always
 * sent, even empty: the server keeps a stored value only when the field is ABSENT (an older page),
 * so a blank box here must arrive as an explicit empty, not as nothing.
 */

interface StageDraft {
  count: string;
  dollars: string;
}
type StagesDraft = Record<TargetStageKey, StageDraft>;

interface MonthDraft {
  reviews: string;
  rating: string;
  audit: string;
}

const INPUT =
  'w-full min-w-[5rem] rounded-md border border-gray-600 bg-gray-900 px-2 py-1 text-right text-[0.8125rem] tabular-nums text-gray-200 ' +
  'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const BUTTON =
  'inline-flex items-center gap-1.5 rounded-md border border-gray-600 px-3 py-1.5 text-xs font-semibold text-gray-200 ' +
  'hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40';
const H4 = "mb-2 flex items-center gap-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-300 before:h-3 before:w-1 before:rounded-full before:bg-blue-500 before:content-['']";

const toStagesDraft = (stages: StageTargets | undefined): StagesDraft =>
  Object.fromEntries(
    TARGET_STAGES.map(({ key }) => [
      key,
      {
        count: stages?.[key]?.monthlyCount === undefined ? '' : String(stages[key]!.monthlyCount),
        dollars: centsToDollarsInput(stages?.[key]?.monthlyValueCents),
      },
    ]),
  ) as StagesDraft;

/** Parse one stage grid. Returns the problems found, named, so the editor can say which box. */
function parseStages(draft: StagesDraft, who: string): { stages: StageTargets; problems: string[] } {
  const stages: StageTargets = {};
  const problems: string[] = [];
  for (const { key, label } of TARGET_STAGES) {
    const count = wholeNumberInput(draft[key].count, 100_000);
    const cents = dollarsInputToCents(draft[key].dollars);
    if (count === null) problems.push(`${who} ${label} deals`);
    if (cents === null) problems.push(`${who} ${label} dollars`);
    const entry: { monthlyCount?: number; monthlyValueCents?: number } = {};
    if (typeof count === 'number') entry.monthlyCount = count;
    if (typeof cents === 'number') entry.monthlyValueCents = cents;
    if (Object.keys(entry).length > 0) stages[key] = entry;
  }
  return { stages, problems };
}

const StageGrid: React.FC<{
  draft: StagesDraft;
  who: string;
  onChange: (key: TargetStageKey, field: keyof StageDraft, value: string) => void;
}> = ({ draft, who, onChange }) => (
  <table className="w-full max-w-xl text-[0.8125rem]">
    <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
      <tr>
        <th className="py-1 pr-3 text-left font-semibold">Stage</th>
        <th className="px-2 py-1 text-right font-semibold">Deals per month</th>
        <th className="px-2 py-1 text-right font-semibold">$ per month</th>
      </tr>
    </thead>
    <tbody>
      {TARGET_STAGES.map(({ key, label }) => (
        <tr key={key}>
          <td className="py-1 pr-3 text-gray-300">{label}</td>
          <td className="px-2 py-1">
            <input
              className={INPUT}
              inputMode="numeric"
              aria-label={`${who} ${label} deals per month`}
              value={draft[key].count}
              onChange={(e) => onChange(key, 'count', e.target.value)}
            />
          </td>
          <td className="px-2 py-1">
            <input
              className={INPUT}
              inputMode="numeric"
              aria-label={`${who} ${label} dollars per month`}
              value={draft[key].dollars}
              onChange={(e) => onChange(key, 'dollars', e.target.value)}
            />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** A referrer the editor may set targets for: the report's partner key and the Referrer Person label. */
export interface PartnerOption {
  key: string;
  label: string;
}

/**
 * The partner options from the Leads & Partners report's partner rows: resolved partners only (a
 * Referrer Ref the export identifies), which are the only keys the server accepts. Exported so the
 * tab that owns the editor can wire the report's rows in with one call.
 */
export function partnerOptionsFromRows(
  rows: ReadonlyArray<{ key: string; partnerLabel: string; issue: string | null }> | undefined
): PartnerOption[] {
  if (!rows) return [];
  const seen = new Set<string>();
  const out: PartnerOption[] = [];
  for (const row of rows) {
    if (row.issue !== null || !row.key.startsWith(PARTNER_TARGET_KEY_PREFIX) || seen.has(row.key)) continue;
    seen.add(row.key);
    out.push({ key: row.key, label: row.partnerLabel });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export interface TargetsEditorProps {
  state: UsePipelineTargetsReturn;
  /** The brokers in this snapshot, for per-broker overrides. Keyed as the report keys them. */
  advisers: ReadonlyArray<{ adviserId: string | null; adviserName: string }>;
  /** The snapshot date: the months offered for monthly inputs end at its month. */
  asAtDate: string;
  /**
   * The referrers a target may be added for, from the Leads & Partners report (partnerOptionsFromRows).
   * Absent means the list is not available on this screen: stored referrer targets still show, and
   * none can be added here.
   */
  partnerOptions?: ReadonlyArray<PartnerOption>;
  /** Called after a successful targets save, so the figures above can refresh. */
  onSaved?: () => void;
}

const TargetsEditor: React.FC<TargetsEditorProps> = ({ state, advisers, asAtDate, partnerOptions, onSaved }) => {
  const { targets, monthlyInputs, saving } = state;

  const [company, setCompany] = useState<StagesDraft>(() => toStagesDraft(undefined));
  const [companyAverage, setCompanyAverage] = useState('');
  const [netSettlements, setNetSettlements] = useState('');
  const [brokers, setBrokers] = useState<Record<string, StagesDraft>>({});
  const [brokerAverage, setBrokerAverage] = useState<Record<string, string>>({});
  const [partners, setPartners] = useState<Record<string, StagesDraft>>({});
  const [addPartner, setAddPartner] = useState('');
  const [green, setGreen] = useState(String(DEFAULT_RAG.greenAtPct));
  const [amber, setAmber] = useState(String(DEFAULT_RAG.amberAtPct));
  const [dip, setDip] = useState('');
  const [mir, setMir] = useState('');
  const [inputTargets, setInputTargets] = useState<MonthDraft>({ reviews: '', rating: '', audit: '' });
  const [addBroker, setAddBroker] = useState('');
  const [months, setMonths] = useState<Record<string, MonthDraft>>({});
  const [targetsMessage, setTargetsMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [inputsMessage, setInputsMessage] = useState<{ ok: boolean; text: string } | null>(null);

  // Follow the stored document into the drafts whenever it changes: first load, a save, and the
  // reload after a version conflict all land here, so the boxes always show what is stored.
  useEffect(() => {
    if (!targets) return;
    setCompany(toStagesDraft(targets.stages));
    setCompanyAverage(centsToDollarsInput(targets.averageDealCents));
    setNetSettlements(centsToDollarsInput(targets.netSettlementsCents));
    setBrokers(
      Object.fromEntries(Object.entries(targets.byBroker ?? {}).map(([key, stages]) => [key, toStagesDraft(stages)])),
    );
    setBrokerAverage(
      Object.fromEntries(
        Object.entries(targets.byBroker ?? {}).map(([key, own]) => [key, centsToDollarsInput(own.averageDealCents)]),
      ),
    );
    setPartners(
      Object.fromEntries(Object.entries(targets.byPartner ?? {}).map(([key, stages]) => [key, toStagesDraft(stages)])),
    );
    setGreen(String(targets.rag?.greenAtPct ?? DEFAULT_RAG.greenAtPct));
    setAmber(String(targets.rag?.amberAtPct ?? DEFAULT_RAG.amberAtPct));
    setDip(targets.sla?.dipHours === undefined ? '' : String(targets.sla.dipHours));
    setMir(targets.sla?.mirHours === undefined ? '' : String(targets.sla.mirHours));
    const given = targets.monthlyInputTargets;
    const fmt = (v: number | undefined) => (v === undefined ? '' : String(v));
    setInputTargets({
      reviews: fmt(given?.googleReviewCount),
      rating: fmt(given?.googleRating),
      audit: fmt(given?.sfgAuditPct),
    });
  }, [targets]);

  const monthKeys = useMemo(() => {
    const keys = new Set([...recentMonthKeys(asAtDate), ...Object.keys(monthlyInputs?.months ?? {})]);
    return [...keys].sort().reverse();
  }, [asAtDate, monthlyInputs]);

  useEffect(() => {
    if (!monthlyInputs) return;
    const fmt = (v: number | undefined) => (v === undefined ? '' : String(v));
    setMonths(
      Object.fromEntries(
        Object.entries(monthlyInputs.months).map(([key, m]) => [
          key,
          { reviews: fmt(m.googleReviewCount), rating: fmt(m.googleRating), audit: fmt(m.sfgAuditPct) },
        ]),
      ),
    );
  }, [monthlyInputs]);

  const nameFor = (key: string): string =>
    advisers.find((a) => (a.adviserId ?? a.adviserName) === key)?.adviserName ?? key;

  const available = advisers.filter((a) => a.adviserId !== null && brokers[a.adviserId] === undefined);

  const partnerNameFor = (key: string): string => partnerOptions?.find((p) => p.key === key)?.label ?? key;
  const availablePartners = (partnerOptions ?? []).filter((p) => partners[p.key] === undefined);
  const partnersAtCap = Object.keys(partners).length >= MAX_PARTNER_OVERRIDES;

  if (state.error) {
    return <p className="text-sm text-amber-300">{state.error}</p>;
  }
  if (!targets || !monthlyInputs) {
    return <p className="text-sm text-gray-400">Loading targets…</p>;
  }

  const saveTargets = async () => {
    setTargetsMessage(null);
    const problems: string[] = [];
    const companyParsed = parseStages(company, 'Company');
    problems.push(...companyParsed.problems);
    const companyAverageCents = dollarsInputToCents(companyAverage);
    if (companyAverageCents === null) problems.push('Company average settled deal dollars');
    const netSettlementsCents = dollarsInputToCents(netSettlements);
    if (netSettlementsCents === null) problems.push('Company net settlements dollars');
    const byBroker: Record<string, BrokerTargets> = {};
    for (const [key, draft] of Object.entries(brokers)) {
      const parsed = parseStages(draft, nameFor(key));
      problems.push(...parsed.problems);
      const averageCents = dollarsInputToCents(brokerAverage[key] ?? '');
      if (averageCents === null) problems.push(`${nameFor(key)} average settled deal dollars`);
      const own: BrokerTargets = { ...parsed.stages };
      if (typeof averageCents === 'number') own.averageDealCents = averageCents;
      if (Object.keys(own).length > 0) byBroker[key] = own;
    }
    const byPartner: Record<string, StageTargets> = {};
    for (const [key, draft] of Object.entries(partners)) {
      const parsed = parseStages(draft, partnerNameFor(key));
      problems.push(...parsed.problems);
      if (Object.keys(parsed.stages).length > 0) byPartner[key] = parsed.stages;
    }
    const g = wholeNumberInput(green, 100);
    const a = wholeNumberInput(amber, 100);
    if (typeof g !== 'number') problems.push('Green threshold (0 to 100)');
    if (typeof a !== 'number') problems.push('Amber threshold (0 to 100)');
    if (typeof g === 'number' && typeof a === 'number' && a > g) {
      problems.push('Amber threshold must be at or below green');
    }
    const dipHours = wholeNumberInput(dip, 24 * 365);
    const mirHours = wholeNumberInput(mir, 24 * 365);
    if (dipHours === null) problems.push('DIP SLA hours');
    if (mirHours === null) problems.push('MIR SLA hours');
    const reviewsTarget = wholeNumberInput(inputTargets.reviews, 1_000_000);
    const ratingTarget = boundedDecimalInput(inputTargets.rating, 0, 5);
    const auditTarget = boundedDecimalInput(inputTargets.audit, 0, 100);
    if (reviewsTarget === null) problems.push('Google reviews target (a whole number)');
    if (ratingTarget === null) problems.push('Google rating target (0 to 5)');
    if (auditTarget === null) problems.push('SFG audit target (0 to 100)');
    const monthlyInputTargets: MonthlyInputTargets = {
      ...(typeof reviewsTarget === 'number' ? { googleReviewCount: reviewsTarget } : {}),
      ...(typeof ratingTarget === 'number' ? { googleRating: ratingTarget } : {}),
      ...(typeof auditTarget === 'number' ? { sfgAuditPct: auditTarget } : {}),
    };

    if (problems.length > 0) {
      setTargetsMessage({
        ok: false,
        text: `Numbers only, blank for none. Check: ${problems.join(', ')}.`,
      });
      return;
    }
    const outcome = await state.saveTargets({
      stages: companyParsed.stages,
      ...(Object.keys(byBroker).length > 0 ? { byBroker } : {}),
      rag: { greenAtPct: g as number, amberAtPct: a as number },
      sla: {
        ...(typeof dipHours === 'number' ? { dipHours } : {}),
        ...(typeof mirHours === 'number' ? { mirHours } : {}),
      },
      // Always sent, even empty: the server stores the whole document, so leaving it out clears it.
      monthlyInputTargets,
      // Always sent, null or {} for blank: the server keeps a stored value only when the field is absent.
      averageDealCents: typeof companyAverageCents === 'number' ? companyAverageCents : null,
      netSettlementsCents: typeof netSettlementsCents === 'number' ? netSettlementsCents : null,
      byPartner,
    });
    setTargetsMessage(outcome.ok ? { ok: true, text: 'Targets saved.' } : { ok: false, text: outcome.message ?? 'Not saved.' });
    if (outcome.ok) onSaved?.();
  };

  const saveInputs = async () => {
    setInputsMessage(null);
    const problems: string[] = [];
    const out: Record<string, MonthlyInput> = {};
    for (const [key, draft] of Object.entries(months)) {
      const reviews = wholeNumberInput(draft.reviews, 1_000_000);
      const rating = boundedDecimalInput(draft.rating, 0, 5);
      const audit = boundedDecimalInput(draft.audit, 0, 100);
      if (reviews === null) problems.push(`${monthLabel(key)} reviews`);
      if (rating === null) problems.push(`${monthLabel(key)} rating (0 to 5)`);
      if (audit === null) problems.push(`${monthLabel(key)} SFG audit (0 to 100)`);
      const entry: MonthlyInput = {};
      if (typeof reviews === 'number') entry.googleReviewCount = reviews;
      if (typeof rating === 'number') entry.googleRating = rating;
      if (typeof audit === 'number') entry.sfgAuditPct = audit;
      if (Object.keys(entry).length > 0) out[key] = entry;
    }
    if (problems.length > 0) {
      setInputsMessage({ ok: false, text: `Check: ${problems.join(', ')}.` });
      return;
    }
    const outcome = await state.saveMonthlyInputs(out);
    setInputsMessage(outcome.ok ? { ok: true, text: 'Monthly inputs saved.' } : { ok: false, text: outcome.message ?? 'Not saved.' });
  };

  const setMonth = (key: string, field: keyof MonthDraft, value: string) =>
    setMonths((prev) => ({ ...prev, [key]: { ...(prev[key] ?? { reviews: '', rating: '', audit: '' }), [field]: value } }));

  return (
    <div className="space-y-6">
      <div>
        <div className={H4}>Company targets, per month</div>
        <p className="mb-2 text-xs text-gray-500">
          Blank means no target: the tile says so rather than grading against zero. Dollars in whole
          dollars.
        </p>
        <StageGrid
          draft={company}
          who="Company"
          onChange={(key, field, value) => setCompany((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }))}
        />
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-400">
          <label className="flex flex-col gap-1">
            Average settled deal ($)
            <input
              className={INPUT}
              inputMode="numeric"
              aria-label="Company average settled deal dollars"
              value={companyAverage}
              onChange={(e) => setCompanyAverage(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            {`Net settlements, ${netSettlementsHeadlineLabel()} ($)`}
            <input
              className={INPUT}
              inputMode="numeric"
              aria-label="Company net settlements dollars"
              value={netSettlements}
              onChange={(e) => setNetSettlements(e.target.value)}
            />
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Average settled deal grades the broker table and the Board pack per deal, so it is not
          pro-rated to a window. Net settlements is graded on the Board pack against the{' '}
          {netSettlementsHeadlineLabel()} from the lender commission statements, the pack&apos;s
          headline figure.
        </p>
      </div>

      <div>
        <div className={H4}>Broker targets, per month</div>
        <p className="mb-2 text-xs text-gray-500">
          A broker is graded only against their own targets. For a broker without them, Deal movements
          says &ldquo;No target set for this broker&rdquo; once and grades nothing, never against the
          company&apos;s figure.
        </p>
        {Object.keys(brokers).length === 0 && <p className="mb-2 text-xs text-gray-500">No broker targets yet.</p>}
        <div className="space-y-4">
          {Object.entries(brokers).map(([key, draft]) => (
            <div key={key} className="rounded-lg border border-blue-500/20 bg-blue-500/[0.04] p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <b className="text-[0.8125rem] text-gray-200">{nameFor(key)}</b>
                <button
                  type="button"
                  className={BUTTON}
                  aria-label={`Remove targets for ${nameFor(key)}`}
                  onClick={() => {
                    setBrokers((prev) => {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    });
                    setBrokerAverage((prev) => {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    });
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>
              <StageGrid
                draft={draft}
                who={nameFor(key)}
                onChange={(stage, field, value) =>
                  setBrokers((prev) => ({ ...prev, [key]: { ...prev[key], [stage]: { ...prev[key][stage], [field]: value } } }))
                }
              />
              <label className="mt-2 flex flex-col gap-1 text-xs text-gray-400 sm:max-w-xs">
                Average settled deal ($)
                <input
                  className={INPUT}
                  inputMode="numeric"
                  aria-label={`${nameFor(key)} average settled deal dollars`}
                  value={brokerAverage[key] ?? ''}
                  onChange={(e) => setBrokerAverage((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </label>
            </div>
          ))}
        </div>
        {available.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              aria-label="Broker to add targets for"
              value={addBroker}
              onChange={(e) => setAddBroker(e.target.value)}
              className="rounded-md border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-[0.8125rem] text-gray-200"
            >
              <option value="">Choose a broker</option>
              {available.map((a) => (
                <option key={a.adviserId as string} value={a.adviserId as string}>
                  {a.adviserName}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={BUTTON}
              disabled={addBroker === ''}
              onClick={() => {
                setBrokers((prev) => ({ ...prev, [addBroker]: toStagesDraft(undefined) }));
                setAddBroker('');
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add broker targets
            </button>
          </div>
        )}
      </div>

      <div>
        <div className={H4}>Referrer targets, per month</div>
        <p className="mb-2 text-xs text-gray-500">
          Leads, submissions, approvals and settlements from one referral partner, graded on Leads &amp;
          Partners against the partner&apos;s own figures. A referrer without them reads &ldquo;No target
          set for this referrer&rdquo;.
        </p>
        {Object.keys(partners).length === 0 && <p className="mb-2 text-xs text-gray-500">No referrer targets yet.</p>}
        <div className="space-y-4">
          {Object.entries(partners).map(([key, draft]) => (
            <div key={key} className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] p-3" data-testid="partner-targets">
              <div className="mb-2 flex items-center justify-between gap-3">
                <b className="text-[0.8125rem] text-gray-200">{partnerNameFor(key)}</b>
                <button
                  type="button"
                  className={BUTTON}
                  aria-label={`Remove targets for ${partnerNameFor(key)}`}
                  onClick={() =>
                    setPartners((prev) => {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    })
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>
              <StageGrid
                draft={draft}
                who={partnerNameFor(key)}
                onChange={(stage, field, value) =>
                  setPartners((prev) => ({ ...prev, [key]: { ...prev[key], [stage]: { ...prev[key][stage], [field]: value } } }))
                }
              />
            </div>
          ))}
        </div>
        {partnerOptions === undefined ? (
          <p className="mt-2 text-xs text-gray-500">
            The referrer list is not available on this screen, so no referrer can be added here.
          </p>
        ) : partnersAtCap ? (
          <p className="mt-2 text-xs text-gray-500">{`At most ${MAX_PARTNER_OVERRIDES} referrers can carry targets.`}</p>
        ) : availablePartners.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              aria-label="Referrer to add targets for"
              value={addPartner}
              onChange={(e) => setAddPartner(e.target.value)}
              className="rounded-md border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-[0.8125rem] text-gray-200"
            >
              <option value="">Choose a referrer</option>
              {availablePartners.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={BUTTON}
              disabled={addPartner === ''}
              onClick={() => {
                setPartners((prev) => ({ ...prev, [addPartner]: toStagesDraft(undefined) }));
                setAddPartner('');
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add referrer targets
            </button>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            Every referrer the Sales export identifies by Referrer Ref already has targets, or none is recorded yet.
          </p>
        )}
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <div className={H4}>Red, amber, green</div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
            <label className="flex flex-col gap-1">
              Green at or above (%)
              <input className={INPUT} inputMode="numeric" value={green} onChange={(e) => setGreen(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              Amber at or above (%)
              <input className={INPUT} inputMode="numeric" value={amber} onChange={(e) => setAmber(e.target.value)} />
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500">Below amber is red.</p>
        </div>
        <div>
          <div className={H4}>SLA targets</div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
            <label className="flex flex-col gap-1">
              DIP (hours)
              <input className={INPUT} inputMode="numeric" value={dip} onChange={(e) => setDip(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              MIR (hours)
              <input className={INPUT} inputMode="numeric" value={mir} onChange={(e) => setMir(e.target.value)} />
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Stored now. Nothing is graded against them until the Operations overview can measure DIP and
            MIR times.
          </p>
        </div>
      </div>

      <div>
        <div className={H4}>Monthly input targets</div>
        <div className="flex flex-wrap gap-4 text-xs text-gray-400">
          <label className="flex flex-col gap-1">
            Google reviews
            <input
              className={INPUT}
              inputMode="numeric"
              aria-label="Google reviews target"
              value={inputTargets.reviews}
              onChange={(e) => setInputTargets((prev) => ({ ...prev, reviews: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            Google rating (0 to 5)
            <input
              className={INPUT}
              inputMode="decimal"
              aria-label="Google rating target"
              value={inputTargets.rating}
              onChange={(e) => setInputTargets((prev) => ({ ...prev, rating: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            SFG audit %
            <input
              className={INPUT}
              inputMode="decimal"
              aria-label="SFG audit target"
              value={inputTargets.audit}
              onChange={(e) => setInputTargets((prev) => ({ ...prev, audit: e.target.value }))}
            />
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Graded on Operations, Overview, against the month&apos;s entered figure with the thresholds
          above. Blank means no target, and that tile shows no status.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={BUTTON} disabled={saving} onClick={() => void saveTargets()}>
          <Save className="h-3.5 w-3.5" />
          Save targets
        </button>
        {targetsMessage && (
          <span role="status" className={`text-xs ${targetsMessage.ok ? 'text-green-300' : 'text-amber-300'}`}>
            {targetsMessage.text}
          </span>
        )}
      </div>

      <div className="border-t border-gray-700/70 pt-5">
        <div className={H4}>Monthly inputs</div>
        <p className="mb-2 text-xs text-gray-500">
          Figures no export carries, typed in once a month. Blank means not recorded.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full max-w-2xl text-[0.8125rem]">
            <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
              <tr>
                <th className="py-1 pr-3 text-left font-semibold">Month</th>
                <th className="px-2 py-1 text-right font-semibold">Google reviews</th>
                <th className="px-2 py-1 text-right font-semibold">Google rating (0 to 5)</th>
                <th className="px-2 py-1 text-right font-semibold">SFG audit %</th>
              </tr>
            </thead>
            <tbody>
              {monthKeys.map((key) => {
                const draft = months[key] ?? { reviews: '', rating: '', audit: '' };
                const label = monthLabel(key);
                return (
                  <tr key={key}>
                    <td className="py-1 pr-3 text-gray-300">{label}</td>
                    <td className="px-2 py-1">
                      <input className={INPUT} inputMode="numeric" aria-label={`Google reviews ${label}`} value={draft.reviews} onChange={(e) => setMonth(key, 'reviews', e.target.value)} />
                    </td>
                    <td className="px-2 py-1">
                      <input className={INPUT} inputMode="decimal" aria-label={`Google rating ${label}`} value={draft.rating} onChange={(e) => setMonth(key, 'rating', e.target.value)} />
                    </td>
                    <td className="px-2 py-1">
                      <input className={INPUT} inputMode="decimal" aria-label={`SFG audit ${label}`} value={draft.audit} onChange={(e) => setMonth(key, 'audit', e.target.value)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" className={BUTTON} disabled={saving} onClick={() => void saveInputs()}>
            <Save className="h-3.5 w-3.5" />
            Save monthly inputs
          </button>
          {inputsMessage && (
            <span role="status" className={`text-xs ${inputsMessage.ok ? 'text-green-300' : 'text-amber-300'}`}>
              {inputsMessage.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TargetsEditor;
